import api from "../../lib/api"
import { SearchMultiSelect } from "./SearchMultiSelect"

export type StudentOption = {
  id: string
  name: string
  email: string
  institution?: string | null
  documentNumber?: string | null
}

export type StudentAssignmentPickerProps = {
  /** Students already selected/pending (small list) — excluded from search results and shown as chips. */
  selectedStudents: StudentOption[]
  /** Student ids to exclude from search results beyond the selected ones (e.g. already on the roster). */
  excludeIds?: string[]
  onToggleStudent: (student: StudentOption) => void
}

type StudentSearchOption = StudentOption & { label: string; sublabel: string }

/** Email + institución/documento cuando existan, para distinguir estudiantes con el mismo nombre. */
function studentSublabel(s: StudentOption): string {
  return [s.email, s.institution, s.documentNumber ? `Doc. ${s.documentNumber}` : null].filter(Boolean).join(" · ")
}

function toSearchOption(s: StudentOption): StudentSearchOption {
  return { ...s, label: s.name, sublabel: studentSublabel(s) }
}

/**
 * Buscador tipo-ahead (servidor, sin precargar todos los estudiantes —
 * seedbed-group-manager) + selección múltiple. Muestra institución/documento
 * además de email para distinguir estudiantes con el mismo nombre. Reutilizado
 * por GroupStudentsView y por el gestor de grupos al crear/editar.
 */
export function StudentAssignmentPicker({
  selectedStudents,
  excludeIds = [],
  onToggleStudent,
}: StudentAssignmentPickerProps) {
  async function onSearch(query: string): Promise<StudentSearchOption[]> {
    const res = await api.get("/admin/users", { params: { role: "student", search: query, limit: 20 } })
    type UserRow = { id: string; name: string; email: string; studentProfile?: { institution?: string | null; documentNumber?: string | null } | null }
    const users: UserRow[] = res.data?.data?.users ?? []
    const excluded = new Set(excludeIds)
    return users
      .filter((u) => !excluded.has(u.id))
      .map((u) =>
        toSearchOption({
          id: u.id,
          name: u.name,
          email: u.email,
          institution: u.studentProfile?.institution,
          documentNumber: u.studentProfile?.documentNumber,
        })
      )
  }

  return (
    <SearchMultiSelect
      selectedOptions={selectedStudents.map(toSearchOption)}
      onToggle={onToggleStudent}
      onSearch={onSearch}
      placeholder="Buscar estudiante por nombre o email..."
      emptyMessage="No hay estudiantes con ese criterio."
    />
  )
}

export default StudentAssignmentPicker
